import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ImagePlus, Loader2, MessageSquareQuote } from 'lucide-react';
import TestimonialCard from '../ui/TestimonialCard';
import { publicService } from '../../services/public.service';
import { showAppAlert } from '../../utils/appAlert';

const Testimonials = ({ testimonials: propTestimonials = [], isLoading }) => {
  const [quote, setQuote] = useState('');
  const [role, setRole] = useState('');
  const [image, setImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const displayTestimonials = propTestimonials;
  const isLoggedIn = Boolean(localStorage.getItem('token'));

  const handleSubmit = async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    try {
      setSubmitting(true);
      await publicService.submitTestimonial({ quote, role, image });
      setQuote('');
      setRole('');
      setImage(null);
      if (formElement?.reset) {
        formElement.reset();
      }
      showAppAlert({
        type: 'success',
        title: 'Testimonial Submitted',
        message: 'Your testimonial has been sent for admin review and will appear after approval.'
      });
    } catch (error) {
      showAppAlert({
        type: 'error',
        title: 'Submission Failed',
        message: error.message || 'Failed to submit testimonial.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-8 sm:py-10 bg-[#faf8f5]" aria-labelledby="testimonials-heading">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6">
          <div><p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-orange-500 mb-2">Stories from the road</p><h2 id="testimonials-heading" className="text-2xl sm:text-3xl font-bold tracking-tight text-brand-black">What Our Customers Say</h2></div>
          <p className="max-w-xs text-sm leading-relaxed text-gray-500">Different journeys. Shared experiences.<br />Hear from the people who ride with us.</p>
        </div>
        {isLoading && !displayTestimonials.length ? <p role="status" className="rounded-2xl border border-gray-200 bg-white p-5 text-sm text-gray-500">Loading customer stories…</p> : displayTestimonials.length ? <div className="grid md:grid-cols-3 gap-4">{displayTestimonials.slice(0, 3).map((t, index) => <TestimonialCard key={t.id || index} {...t} />)}</div> : <p className="rounded-2xl border border-gray-200 bg-white p-5 text-sm text-gray-500">Your story could be the first. Tell us about your journey with Go Rides.</p>}
        <div className="mt-5 rounded-2xl border border-gray-200 bg-white grid lg:grid-cols-[0.8fr_1.2fr] overflow-hidden">
          <div className="p-5 sm:p-6 bg-brand-black text-white">
            <MessageSquareQuote className="w-7 h-7 text-brand-orange-500 mb-4" aria-hidden="true" />
            <h3 className="text-xl font-bold">Share Your Experience</h3>
            <p className="text-sm text-gray-300 leading-relaxed mt-3">What made your journey memorable? Your feedback helps others plan their next ride.</p>
            <p className="text-xs text-gray-400 mt-4 leading-relaxed">Testimonials are reviewed before publication. A photo is optional.</p>
          </div>
          <div className="p-5 sm:p-6">
            {!isLoggedIn ? <div className="flex h-full flex-col items-start justify-center gap-4"><p className="text-sm text-gray-600">Log in to share your story with the Go Rides community.</p><Link to="/login" className="rounded-xl bg-brand-orange-500 hover:bg-brand-orange-600 px-5 py-3 text-sm font-semibold text-white">Log in to share</Link></div> :
              <form onSubmit={handleSubmit} className="space-y-4">
                <div><label htmlFor="testimonial-quote" className="block text-sm font-semibold text-gray-700 mb-2">Your experience</label><textarea id="testimonial-quote" value={quote} onChange={event => setQuote(event.target.value)} rows={3} required disabled={submitting} placeholder="Tell us about your car, driver, or journey…" className="w-full rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500" /></div>
                <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-end"><div><label htmlFor="testimonial-role" className="block text-sm font-semibold text-gray-700 mb-2">About you <span className="text-xs font-normal text-gray-400">(optional)</span></label><input id="testimonial-role" value={role} onChange={event => setRole(event.target.value)} disabled={submitting} placeholder="e.g. Business traveller" className="w-full rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-brand-orange-500" /></div>
                  <label className="relative inline-flex items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-600 cursor-pointer hover:border-brand-orange-500 focus-within:ring-2 focus-within:ring-orange-200"><ImagePlus className="h-4 w-4" aria-hidden="true" />{image ? 'Change photo' : 'Add photo'}<input aria-label="Add testimonial photo" type="file" accept="image/*" disabled={submitting} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" onChange={event => setImage(event.target.files?.[0] || null)} /></label>
                </div>
                {image && <p className="text-xs text-gray-500 break-all">{image.name}</p>}
                <button type="submit" disabled={submitting} className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange-500 hover:bg-brand-orange-600 px-5 py-3 text-sm font-semibold text-white disabled:opacity-60">{submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}{submitting ? 'Submitting…' : 'Submit testimonial'}</button>
              </form>}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
